import { TaskCenterTypes, useTaskCenterMutations } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useTaskCenterStore } from '../provider';
import { Citation } from '../types';
import { getUpdatedFilters } from '../utils';

interface SetMessageCitationProps {
  sourceId: string;
  source: Citation;
  personName?: string;
  locationId: string;
}

export const useTaskCitation = () => {
  const { t } = useTranslation('task-center');
  const { getTaskByTaskDetailsMutation } = useTaskCenterMutations();
  const {
    selectedTask,
    setSelectedTask,
    setTaskMode,
    setIsTaskCenterOpen,
    isTaskCenterOpen,
    locationIds: selectedLocationIds,
  } = useTaskCenterStore([
    'setSelectedTask',
    'setTaskMode',
    'selectedTask',
    'setIsTaskCenterOpen',
    'isTaskCenterOpen',
    'locationIds',
  ]);

  const setMessageCitation = ({ sourceId, source, personName, locationId }: SetMessageCitationProps) => {
    const title = t(`Inbox: New Task ({{personTitle}})`, {
      personTitle: !!personName ? personName : formatPhoneNumber(source.phone_no) ?? 'Unknown User',
    });
    if (!!selectedTask?.sourceId) {
      // marking the selectedTask as null so that we override task the when we API fetches the updated details
      setSelectedTask(null);
    }
    setSelectedTask({
      source: TaskCenterTypes.TaskSource.SOURCE_MESSAGING,
      sourceId,
      metadata: JSON.stringify({ source }),
      title,
      locationId: locationId,
      type: TaskCenterTypes.TaskType.TYPE_UNKNOWN,
    });
    setTaskMode('citation');
    !isTaskCenterOpen && setIsTaskCenterOpen(true);
  };

  const setTaskBySourceId = async (
    { source, sourceId, status, assignee, priority }: Partial<TaskCenterTypes.Task>,
    locationId: string[] = selectedLocationIds
  ) => {
    const filter = getUpdatedFilters(
      {
        [TaskCenterTypes.FilterType_index.FILTER_BY_LOCATION]: { locationId },
      },
      { source, sourceId, status, assignee, priority },
      locationId
    );
    return getTaskByTaskDetailsMutation.mutateAsync(
      { filter, includeMetadata: true },
      {
        onError: (error) => {
          throw Error(String(error));
        },
        onSuccess: (res) => {
          if (res?.tasks && (res.tasks.length > 1 || res.tasks.length === 0)) {
            throw new Error(
              res.tasks.length === 0 ? t('Task not found') : t('Multiple tasks found with same sourceId')
            );
          } else {
            if (res?.tasks?.[0]) {
              setSelectedTask(res.tasks[0]);
              setTaskMode('citation');
              !isTaskCenterOpen && setIsTaskCenterOpen(true);
            }
          }
        },
      }
    );
  };

  return { setMessageCitation, setTaskBySourceId };
};
